import { makeStyles } from '@material-ui/core';
import { Events } from '@type-r/models';
import { useLinked } from '@type-r/react';
import * as React from 'react';
import {asCurrency, asTimelessDate, CompactInput, Flex, PrimaryButton, Tile, TileField} from 'lqx-react';
import { GetInvoiceStatusAsText, InvoiceModel } from '../../../../api/invoice';
import { InvoiceStateFlow, invoiceStatusColor, PromiseToPayDateSelectedInvoiceIdContext } from './common';
import { useContext } from 'react';
import { InblockIcon } from 'common/invoice-aging/iconography';

const useStyles = makeStyles({
    acceptButtonContainer: {
        flexDirection: 'row',
        display: 'inline-flex',

        '& > *': {
            marginTop: 'auto',
            marginBottom: 'auto'
        }
    }
});

interface AcceptInvoiceControlsParams {
    invoice: InvoiceModel;
    disableAcceptButton: boolean;
}

const AcceptInvoiceControls = ({invoice, disableAcceptButton}: AcceptInvoiceControlsParams) => {
    const classes = useStyles();
    const activeInvoiceId = useContext(PromiseToPayDateSelectedInvoiceIdContext);
    const otherPromiseToPayDateSelected = activeInvoiceId.value !== null && activeInvoiceId.value !== invoice.id;
    const $acceptButtonClicked = useLinked(false);
    
    const $promiseToPayDate = invoice.$.promiseToPayDateUtc
        .check(newDate => !$acceptButtonClicked.value || !!newDate)
        .onChange(newDate => {
            activeInvoiceId.set(newDate ? invoice.id : null);
        });


    return (
        <Flex row
            onClick={ e => e.stopPropagation() }
            className={ classes.acceptButtonContainer }
        >
            <CompactInput date
                label='Promise to Pay'
                minDate={ new Date() }
                $value={ $promiseToPayDate }
                disabled={ otherPromiseToPayDateSelected }
            />

            <PrimaryButton
                variant="contained"
                disabled={ disableAcceptButton || otherPromiseToPayDateSelected }
                onClick={async () => {
                    $acceptButtonClicked.set(true);
                    await invoice.accept();
                    activeInvoiceId.set(null);
                }}
            >
                Accept
            </PrimaryButton>
        </Flex>
    );
};

export const InvoiceTile = disableAcceptButton => ({ data, style, index }) => {
    const invoice: InvoiceModel = data.items[ index ],
        $checked = data.selected.$includes( invoice );

    const status = invoice.calculatedStatus;

    const isCustomerOrSupersupplier = invoice.isParticipantApprover;
    const showApprovalControls  = invoice.canBeAccepted && isCustomerOrSupersupplier;
    const inblockIconClasses = inblockIconStyles();
    const displayInblockIcon = invoice.source == 'OM' || invoice.source == 'DIGI';

    return (<Tile large={ isCustomerOrSupersupplier }
            $checked={ $checked }
            onClick={ () => Events.trigger( 'navigate', `/invoices/details/${invoice.id}`, null, {returnUrl : window.location.pathname})}
            style={ style }
            icon= {displayInblockIcon ? <InblockIcon src={ '/assets/images/inblock_logo.png' } classes={inblockIconClasses} /> : null }
            color={ invoiceStatusColor( status ) }
            leftTitle={ GetInvoiceStatusAsText( status ) }
            left={
              asTimelessDate( invoice.invoiceDueDateUtc ) || `-`
            }
            title={ invoice.title }
            right={ <InvoiceStateFlow invoice={ invoice } /> }
            extra={
                showApprovalControls ? <>
                    <Flex grow/>
                    <AcceptInvoiceControls
                        invoice={ invoice }
                        disableAcceptButton={ disableAcceptButton }
                    />
                </> : null
            }
        >
            <TileField label='INV #'>
                {invoice.invoiceNumber}
            </TileField>

            <TileField label='PO #'>
                {invoice.poNumber}
            </TileField>

            <TileField label='INV AMT'>
                { asCurrency( invoice.invoicedAmount )}
            </TileField>   
            {invoice.showPromiseToPayDate && <TileField label='PROMISE TO PAY'>{ asTimelessDate( invoice.promiseToPayDateUtc )}</TileField>}         
        </Tile>
    );
};

const inblockIconStyles = makeStyles({
    root: {
        width: '30px',
        height: '30px',
        marginLeft: '20px'
    }
});